/**
 * Generic polled-data hook with three industry-standard guards (see plan F7/F12):
 *
 *  1. **Tab-visibility pause** — when `document.hidden` becomes true, the timer
 *     pauses. Resumes + fires an immediate refresh on `visibilitychange` →
 *     visible. Cuts cost ~50% for inactive tabs.
 *
 *  2. **Window-focus instant refresh** — `window.focus` triggers a refresh
 *     immediately rather than waiting for the next tick.
 *
 *  3. **Multi-tab leader election via BroadcastChannel** — when two tabs of
 *     the same page are open in the same browser session, only ONE polls.
 *     The leader broadcasts the latest payload to followers, who render it
 *     without making their own request. Cuts cost ~50% on power-user browsing.
 *
 * The hook is intentionally generic — any caller passes a fetcher fn + a
 * BroadcastChannel name. Used by AccountManagerTerminalPage; can be reused by
 * future polling surfaces (Schedule, Positions).
 *
 * Returns `{ data, error, isLoading, isPaused, refresh }`. `refresh` is a
 * stable no-arg function that forces an immediate fetch from the leader tab.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UsePolledDataOptions<T> {
  fetcher: () => Promise<T>;
  intervalMs: number;
  /**
   * BroadcastChannel name. Leave undefined to disable multi-tab dedup
   * (e.g. when fetcher arguments differ per tab). When set, all tabs in the
   * same browser sharing this channel name will only poll from the leader.
   */
  channelName?: string;
  /** When false, suppresses fetches entirely (e.g. while auth is loading). */
  enabled?: boolean;
}

export interface UsePolledDataResult<T> {
  data: T | null;
  error: string | null;
  isLoading: boolean;
  isPaused: boolean;
  /** Force an immediate fetch on the leader tab. */
  refresh: () => void;
}

interface BroadcastMessage<T> {
  type: 'data' | 'leader-claim' | 'leader-yield';
  payload?: T;
  tabId?: string;
}

const _randomTabId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function usePolledData<T>(opts: UsePolledDataOptions<T>): UsePolledDataResult<T> {
  const { fetcher, intervalMs, channelName, enabled = true } = opts;

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  // Refs so the polling loop can read fresh values without re-creating the
  // setInterval on every render.
  const fetcherRef = useRef(fetcher);
  const enabledRef = useRef(enabled);
  useEffect(() => { fetcherRef.current = fetcher; }, [fetcher]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const tabId = useRef<string>(_randomTabId()).current;
  const isLeaderRef = useRef<boolean>(true); // optimistic; corrected by leader-claim race
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Single fetch — runs on leader only. Followers receive data via broadcast.
  const doFetch = useCallback(async () => {
    if (!enabledRef.current) return;
    if (!isLeaderRef.current) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current();
      setData(result);
      // Broadcast to followers so they render the same payload.
      if (channelRef.current) {
        const msg: BroadcastMessage<T> = { type: 'data', payload: result, tabId };
        channelRef.current.postMessage(msg);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [tabId]);

  // BroadcastChannel setup + leader election. The simplest stable scheme:
  // every tab posts a `leader-claim` on mount; the highest tabId wins. If a
  // leader unmounts, surviving tabs detect via timeout and re-elect.
  useEffect(() => {
    if (!channelName || typeof BroadcastChannel === 'undefined') {
      // Single-tab mode — this tab is always the leader.
      isLeaderRef.current = true;
      return;
    }
    const channel = new BroadcastChannel(channelName);
    channelRef.current = channel;

    let highestSeenTabId = tabId;
    channel.onmessage = (ev: MessageEvent<BroadcastMessage<T>>) => {
      const msg = ev.data;
      if (msg.type === 'data' && msg.payload !== undefined) {
        // Follower receives a leader's latest payload — render it.
        setData(msg.payload);
        setError(null);
        setIsLoading(false);
      } else if (msg.type === 'leader-claim' && msg.tabId) {
        // Election: highest tabId wins. Stable across re-renders.
        if (msg.tabId > highestSeenTabId) {
          highestSeenTabId = msg.tabId;
          if (msg.tabId !== tabId) {
            isLeaderRef.current = false;
          }
        }
      } else if (msg.type === 'leader-yield' && msg.tabId === highestSeenTabId) {
        // Previous leader gave up (likely unmount). Re-claim.
        highestSeenTabId = tabId;
        isLeaderRef.current = true;
        doFetch();
      }
    };
    // Announce our claim. If a higher tabId is already in the channel it will
    // overrule us via the onmessage handler above.
    channel.postMessage({ type: 'leader-claim', tabId });

    return () => {
      // Yield leadership on unmount so survivors can re-elect.
      if (isLeaderRef.current) {
        channel.postMessage({ type: 'leader-yield', tabId });
      }
      channel.close();
      channelRef.current = null;
    };
  }, [channelName, tabId, doFetch]);

  // Polling loop with visibility + focus + interval guards.
  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    let timer: number | undefined;
    let stopped = false;

    const tick = async () => {
      if (stopped) return;
      if (document.visibilityState === 'hidden') {
        setIsPaused(true);
        timer = window.setTimeout(tick, intervalMs);
        return;
      }
      setIsPaused(false);
      await doFetch();
      if (!stopped) timer = window.setTimeout(tick, intervalMs);
    };

    // Visibility-resume handler: when the tab becomes visible, fire an
    // immediate refresh and resume the interval cadence.
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !stopped) {
        if (timer) window.clearTimeout(timer);
        tick();
      }
    };
    const onFocus = () => {
      if (!stopped) {
        if (timer) window.clearTimeout(timer);
        tick();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    // Initial fetch on mount.
    tick();

    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [intervalMs, doFetch, enabled]);

  const refresh = useCallback(() => {
    // Manual refresh always runs on this tab regardless of leader status —
    // if a user clicks "Refresh" they expect a fetch from THIS tab.
    const wasLeader = isLeaderRef.current;
    isLeaderRef.current = true;
    doFetch().finally(() => {
      isLeaderRef.current = wasLeader;
    });
  }, [doFetch]);

  return { data, error, isLoading, isPaused, refresh };
}
